import {
  type ApiResponse,
  type PaginatedResponse
} from '../../api-client';

import {
  apiRequest
} from './client';

import type {
  Payment,
  PaymentStatusEnum,
  PaymentStatusView,
  UpdatePaymentInput,
  Refund,
  CreateRefundInput,
  UpdateRefundInput,
  PresignPaymentProofInput,
  PaymentPresignResult,
  VerifyPaymentInput
} from './types/invoices.types';

/** Public - no auth. Used by the gateway checkout redirect pages (success/cancel/error). */
export async function getPaymentStatus(
  reference: string
): Promise<ApiResponse<PaymentStatusView>> {

  return apiRequest<ApiResponse<PaymentStatusView>>(`/payments/status/${ encodeURIComponent(reference) }`);
}

export interface ListPaymentsParams {
  invoiceId?: number
  status?: PaymentStatusEnum
  limit?: number
  offset?: number
}

export async function listPayments(
  params: ListPaymentsParams = {},
  token: string
): Promise<PaginatedResponse<Payment>> {

  const query = new URLSearchParams();

  if (params.invoiceId !== undefined) {

    query.set('invoiceId', String(params.invoiceId));
  }

  if (params.status !== undefined) {

    query.set('status', params.status);
  }

  if (params.limit !== undefined) {

    query.set('limit', String(params.limit));
  }

  if (params.offset !== undefined) {

    query.set('offset', String(params.offset));
  }

  const qs = query.size > 0 ? `?${ query.toString() }` : '';
  return apiRequest<PaginatedResponse<Payment>>(`/payments${ qs }`, undefined, token);
}

export async function getPaymentById(
  id: number,
  token: string
): Promise<ApiResponse<Payment>> {

  return apiRequest<ApiResponse<Payment>>(`/payments/${ id }`, undefined, token);
}

export async function updatePayment(
  id: number,
  input: UpdatePaymentInput,
  token: string
): Promise<ApiResponse<Payment>> {

  return apiRequest<ApiResponse<Payment>>(
    `/payments/${ id }`,
    {
      method: 'PUT',
      body: JSON.stringify(input)
    },
    token
  );
}

export async function listRefunds(
  paymentId: number,
  token: string
): Promise<ApiResponse<Refund[]>> {

  return apiRequest<ApiResponse<Refund[]>>(`/refunds?paymentId=${ paymentId }`, undefined, token);
}

export async function createRefund(
  paymentId: number,
  input: CreateRefundInput,
  token: string
): Promise<ApiResponse<Refund>> {

  return apiRequest<ApiResponse<Refund>>(
    '/refunds',
    {
      method: 'POST',
      body: JSON.stringify({
        ...input,
        paymentId
      })
    },
    token
  );
}

export async function updateRefund(
  refundId: number,
  input: UpdateRefundInput,
  token: string
): Promise<ApiResponse<Refund>> {

  return apiRequest<ApiResponse<Refund>>(
    `/refunds/${ refundId }`,
    {
      method: 'PUT',
      body: JSON.stringify(input)
    },
    token
  );
}

/** Admin only. */
export async function presignPaymentProof(
  id: number,
  input: PresignPaymentProofInput,
  token: string
): Promise<ApiResponse<PaymentPresignResult>> {

  return apiRequest<ApiResponse<PaymentPresignResult>>(
    `/payments/${ id }/proof/presign`,
    {
      method: 'POST',
      body: JSON.stringify(input)
    },
    token
  );
}

/** Admin only. */
export async function confirmPaymentProof(
  id: number,
  fileKey: string,
  token: string
): Promise<ApiResponse<Payment>> {

  return apiRequest<ApiResponse<Payment>>(
    `/payments/${ id }/proof/confirm`,
    {
      method: 'POST',
      body: JSON.stringify({
        fileKey
      })
    },
    token
  );
}

/** Admin only. */
export async function getPaymentProofDownloadUrl(
  id: number,
  token: string
): Promise<ApiResponse<{ downloadUrl: string }>> {

  return apiRequest<ApiResponse<{ downloadUrl: string }>>(`/payments/${ id }/proof/download`, undefined, token);
}

/** Admin only. */
export async function verifyPayment(
  id: number,
  input: VerifyPaymentInput,
  token: string
): Promise<ApiResponse<Payment>> {

  return apiRequest<ApiResponse<Payment>>(
    `/payments/${ id }/verify`,
    {
      method: 'PATCH',
      body: JSON.stringify(input)
    },
    token
  );
}
